import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Breadcrumbs,
  CircularProgress,
  keyframes,
} from "@mui/material";
import Activities from "./components/activities";
import { Column } from "@ant-design/plots";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import DynamicDataGrid from "../../components/dynamicDataGrid";

const shimmerAnimation = keyframes`
  0% { background-position: -800px 0; }
  100% { background-position: 800px 0; }
`;

const ShimmerBox = ({
  width = "100%",
  height = 40,
  borderRadius = 8,
  sx = {},
}) => (
  <Box
    sx={{
      width,
      height,
      borderRadius: `${borderRadius}px`,
      background:
        "linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 50%, #e8e8e8 75%)",
      backgroundSize: "800px 100%",
      animation: `${shimmerAnimation} 1.4s infinite linear`,
      flexShrink: 0,
      ...sx,
    }}
  />
);

// ── Candidate Tiles shimmer — compact size matching actual tiles ──────────
const CandidateTilesShimmer = () => (
  <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
    {/* Employer Portal tile — 70% width, 2 sub-tiles side by side */}
    <Box
      sx={{
        width: "70%",
        backgroundColor: "#F0F7EE",
        borderRadius: "10px",
        border: "1px solid #E2E8F0",
        p: "12px 14px",
      }}
    >
      {/* Header row: title + filter */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <ShimmerBox width={120} height={12} borderRadius={3} />
        <ShimmerBox width={100} height={24} borderRadius={5} />
      </Box>
      {/* Two sub-tiles */}
      <Box sx={{ display: "flex", gap: 1 }}>
        {/* Associated */}
        <Box
          sx={{
            flex: 1,
            p: "10px 12px",
            border: "1px solid #C8E6C9",
            borderRadius: "8px",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: "7px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <ShimmerBox width="32%" height={22} borderRadius={3} />
            <ShimmerBox width="18%" height={11} borderRadius={3} />
          </Box>
          <ShimmerBox width="70%" height={11} borderRadius={3} />
        </Box>
        {/* Not Associated */}
        <Box
          sx={{
            flex: 1,
            p: "10px 12px",
            border: "1px solid #C8E6C9",
            borderRadius: "8px",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: "7px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <ShimmerBox width="22%" height={22} borderRadius={3} />
            <ShimmerBox width="18%" height={11} borderRadius={3} />
          </Box>
          <ShimmerBox width="76%" height={11} borderRadius={3} />
        </Box>
      </Box>
    </Box>

    {/* Jobeze Portal tile — 30% width, 1 sub-tile */}
    <Box
      sx={{
        width: "30%",
        backgroundColor: "#F0F7EE",
        borderRadius: "10px",
        border: "1px solid #E2E8F0",
        p: "12px 14px",
      }}
    >
      {/* Header row: title + filter */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <ShimmerBox width={100} height={12} borderRadius={3} />
        <ShimmerBox width={100} height={24} borderRadius={5} />
      </Box>
      {/* Single sub-tile — blue tint since it's usually selected */}
      <Box
        sx={{
          p: "10px 12px",
          border: "1px solid #C8E6C9",
          borderRadius: "8px",
          backgroundColor: "#e8f0fb",
          display: "flex",
          flexDirection: "column",
          gap: "7px",
        }}
      >
        <ShimmerBox
          width="30%"
          height={22}
          borderRadius={3}
          sx={{
            background:
              "linear-gradient(90deg, #c2d9ef 25%, #b0cce6 50%, #c2d9ef 75%)",
            backgroundSize: "800px 100%",
          }}
        />
        <ShimmerBox
          width="58%"
          height={11}
          borderRadius={3}
          sx={{
            background:
              "linear-gradient(90deg, #c2d9ef 25%, #b0cce6 50%, #c2d9ef 75%)",
            backgroundSize: "800px 100%",
          }}
        />
      </Box>
    </Box>
  </Box>
);

// ── Candidate Chart shimmer — matches blue recruiter bar chart ────────────
const CandidateChartShimmer = () => {
  const bars = [68, 14, 11, 8, 4, 3, 3, 2, 1, 1];
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        pt: 1,
      }}
    >
      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* Y-axis labels */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            pb: "28px",
            pr: 1,
            width: 32,
            flexShrink: 0,
          }}
        >
          {[0, 1, 2, 3, 4, 5].map((_, i) => (
            <ShimmerBox key={i} width={22} height={10} borderRadius={3} />
          ))}
        </Box>
        {/* Bars + x-axis */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "flex-end",
              gap: "10px",
              pb: "4px",
            }}
          >
            {bars.map((h, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: "100%",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <ShimmerBox
                  width="100%"
                  height={`${Math.max(h, 2)}%`}
                  borderRadius="4px 4px 0 0"
                  sx={{
                    minHeight: 4,
                    background:
                      "linear-gradient(90deg, #c2d9ef 25%, #b0cce6 50%, #c2d9ef 75%)",
                    backgroundSize: "800px 100%",
                    animation: `${shimmerAnimation} 1.4s infinite linear`,
                  }}
                />
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              height: "1px",
              background: "#d0d0d0",
              mb: "8px",
              flexShrink: 0,
            }}
          />
          <Box sx={{ display: "flex", gap: "10px", flexShrink: 0 }}>
            {bars.map((_, i) => (
              <ShimmerBox
                key={i}
                width="100%"
                height={11}
                borderRadius={3}
                sx={{ flex: 1 }}
              />
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "8px",
              flexShrink: 0,
            }}
          >
            <ShimmerBox width={72} height={11} borderRadius={3} />
          </Box>
          <Box sx={{ mt: "10px", flexShrink: 0 }}>
            <ShimmerBox width="48%" height={5} borderRadius={3} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Activity shimmer — always shown while activities load ─────────────────
const ActivityShimmer = () => (
  <Box
    sx={{ display: "flex", flexDirection: "column", gap: 2.5, px: 2, pt: 1 }}
  >
    {Array(6)
      .fill(0)
      .map((_, i) => (
        <Box
          key={i}
          sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              flexShrink: 0,
              background:
                "linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 50%, #e8e8e8 75%)",
              backgroundSize: "800px 100%",
              animation: `${shimmerAnimation} 1.4s infinite linear`,
            }}
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              pt: "2px",
            }}
          >
            <ShimmerBox width="52%" height={11} borderRadius={3} />
            <ShimmerBox width="85%" height={14} borderRadius={3} />
          </Box>
        </Box>
      ))}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────

const CandidateDashboard = ({
  counts,
  highlightedTile,
  onTileClick,
  timeDuration,
}) => {
  const nav = useNavigate();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState({
    loading: false,
    activityLoading: true, // activities start with shimmer
  });

  // ── First-load flags ──
  const isChartFirstLoad = useRef(true);
  const [chartFirstLoadDone, setChartFirstLoadDone] = useState(false);

  // chartNumber ref — async callbacks mein stale closure nahi hogi
  const chartNumber = useRef(1);
  const [chartLevel, setChartLevel] = useState(1);

  const setChartNum = (val) => {
    chartNumber.current = val;
    setChartLevel(val);
  };

  const [chartData, setChartData] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([""]);

  // DataGrid states
  const [gridData, setGridData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [disablePrev, setDisablePrev] = useState(true);
  const [disableNext, setDisableNext] = useState(false);
  const [dataGridRecruiterId, setDataGridRecruiterId] = useState(null);
  const [statusIds, setStatusIds] = useState(null);
  const [columns, setColumns] = useState();

  // Tile ya time change hone pe chart reset + fresh fetch
  useEffect(() => {
    setChartNum(1);
    setPage(1);
    fetchRecruiterchart();
  }, [highlightedTile, timeDuration]);

  // Activities sirf ek baar mount pe load karo
  useEffect(() => {
    fetchActivities();
  }, []);

  // Window resize se chart height recalculate
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const onResize = () => forceUpdate((n) => n + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ─────────────────────────── API Functions ────────────────────────────────

  const fetchActivities = async () => {
    setLoading((prev) => ({ ...prev, activityLoading: true }));
    try {
      const response = await axios.get(`dashboard/candidate-activities`, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      if (response.data.success) setActivities(response.data.message);
    } catch (error) {
      console.error(error);
    } finally {
      // After first load, stop activity shimmer permanently
      setLoading((prev) => ({ ...prev, activityLoading: false }));
    }
  };

  const fetchRecruiterchart = async () => {
    setLoading((prev) => ({ ...prev, loading: true }));
    const portalname =
      highlightedTile === "Total Candidates"
        ? "JOBEZE PORTAL"
        : "EMPLOYER PORTAL";
    const duration =
      highlightedTile === "Total Candidates"
        ? timeDuration.JobezePortalTotal
        : timeDuration.EmployerPortalNotAssociated;
    try {
      const response = await axios.post(
        `/dashboard/candidate/recruiters`,
        {
          portal: portalname,
          duration,
          isAssociatedWithJob:
            highlightedTile === "Candidates Associated with Job" ? 1 : 0,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      if (response.data.success) {
        setChartData(response.data.message);
        if (highlightedTile === "Total Candidates")
          setBreadcrumb(["Jobeze Portal", "Associated", "Recruiters"]);
        else if (highlightedTile === "Candidates Associated with Job")
          setBreadcrumb(["Employer Portal", "Associated", "Recruiters"]);
        else setBreadcrumb(["Employer Portal", "Not Associated", "Recruiters"]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, loading: false }));
      // Mark chart first load done after first successful fetch
      if (isChartFirstLoad.current) {
        isChartFirstLoad.current = false;
        setChartFirstLoadDone(true);
      }
    }
  };

  // Chart 2: Candidate status bar chart
  const fetchCandidatestatus = async (currentAxisId) => {
    if (chartNumber.current !== 2) return;
    setLoading((prev) => ({ ...prev, loading: true }));
    const portalname =
      highlightedTile === "Total Candidates"
        ? "JOBEZE PORTAL"
        : "EMPLOYER PORTAL";
    const duration =
      highlightedTile === "Total Candidates"
        ? timeDuration.JobezePortalTotal
        : timeDuration.EmployerPortalNotAssociated;
    const isAssociatedWithJob =
      highlightedTile === "Total Candidates" ||
      highlightedTile === "Candidates Associated with Job"
        ? 1
        : 0;
    setDataGridRecruiterId(currentAxisId);
    try {
      const response = await axios.post(
        `/dashboard/candidate/candidate-status`,
        {
          portal: portalname,
          isAssociatedWithJob,
          recruiter_id: currentAxisId,
          duration,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      if (response.data.success) {
        const data = response.data.message;
        const appliedData = data.find((item) => item.name === "Applied");
        const employerAppliedData = data.find(
          (item) => item.name === "Employer Applied",
        );
        if (appliedData && employerAppliedData) {
          appliedData.count += employerAppliedData.count;
          setChartData(data.filter((item) => item.name !== "Employer Applied"));
        } else if (employerAppliedData && !appliedData) {
          employerAppliedData.name = "Applied";
          setChartData(data);
        } else {
          setChartData(data);
        }
        if (highlightedTile === "Candidates Associated with Job")
          setBreadcrumb([
            "Employer Portal",
            "Associated",
            "Recruiters",
            "Candidate Status",
          ]);
        else if (highlightedTile === "Total Candidates")
          setBreadcrumb([
            "Jobeze Portal",
            "Associated",
            "Recruiters",
            "Candidate Status",
          ]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, loading: false }));
    }
  };

  // Grid 2: Status tabular data (Associated tiles)
  const fetchCandidatestatusGrid = async (currentAxisId, pg) => {
    if (chartNumber.current !== 2) return;
    setLoading((prev) => ({ ...prev, loading: true }));
    const portalname =
      highlightedTile === "Total Candidates"
        ? "JOBEZE PORTAL"
        : "EMPLOYER PORTAL";
    const duration =
      highlightedTile === "Total Candidates"
        ? timeDuration.JobezePortalTotal
        : timeDuration.EmployerPortalNotAssociated;
    const isAssociatedWithJob =
      highlightedTile === "Total Candidates" ||
      highlightedTile === "Candidates Associated with Job"
        ? 1
        : 0;
    try {
      const response = await axios.post(
        `/dashboard/candidate/status-tablular-data/${pg}`,
        {
          portal: portalname,
          isAssociatedWithJob,
          recruiter_id: currentAxisId,
          duration,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      if (response.data.success) {
        const data = response.data.message;
        const rows = data.map((row, index) => ({
          uniqueId: `${index}-${row.id}`,
          id: row.id,
          ...formatRowFields(row),
        }));
        setGridData(rows);
        setDisableNext(data?.length < pageSize);
        setDisablePrev(pg === 1);
        const predefinedColumns = [
          { field: "id", headerName: "Id", width: 150 },
          { field: "Candidate Name", headerName: "Candidate Name", width: 200 },
          { field: "Experience", headerName: "Experience", width: 200 },
          {
            field: "Current Location",
            headerName: "Current Location",
            width: 200,
          },
          { field: "Contact No.", headerName: "Contact No.", width: 200 },
          { field: "Job Id", headerName: "Job Id", width: 200 },
          { field: "Job Name", headerName: "Job Name", width: 200 },
          { field: "Status", headerName: "Status", width: 200 },
        ];
        setColumns(buildColumns(rows, predefinedColumns));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, loading: false }));
    }
  };

  // Grid 3: Status type drill-down (last level)
  const fetchstatusTypeGrid = async (status, pg) => {
    if (chartNumber.current !== 3) return;
    setLoading((prev) => ({ ...prev, loading: true }));
    const portalname =
      highlightedTile === "Total Candidates"
        ? "JOBEZE PORTAL"
        : "EMPLOYER PORTAL";
    const duration =
      highlightedTile === "Total Candidates"
        ? timeDuration.JobezePortalTotal
        : timeDuration.EmployerPortalNotAssociated;
    const isAssociatedWithJob =
      highlightedTile === "Total Candidates" ||
      highlightedTile === "Candidates Associated with Job"
        ? 1
        : 0;
    setStatusIds(status);
    try {
      const response = await axios.post(
        `/dashboard/candidate/particular-status-data/${pg}`,
        {
          portal: portalname,
          isAssociatedWithJob,
          duration,
          recruiter_id: dataGridRecruiterId,
          status,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      if (response.data.success) {
        const data = response.data.message;
        const rows = data.map((row, index) => ({
          uniqueId: `${index}-${row.id}`,
          id: row.id,
          ...formatRowFields(row),
        }));
        setGridData(rows);
        setDisableNext(data?.length < pageSize);
        setDisablePrev(pg === 1);
        setBreadcrumb(
          highlightedTile === "Total Candidates"
            ? [
                "Jobeze Portal",
                "Associated",
                "Recruiters",
                "Candidate Status",
                "Status Type",
              ]
            : [
                "Employer Portal",
                "Associated",
                "Recruiters",
                "Candidate Status",
                "Status Type",
              ],
        );
        const predefinedColumns = [
          { field: "Candidate Name", headerName: "Candidate Name", width: 200 },
          { field: "Experience", headerName: "Experience", width: 150 },
          {
            field: "Current Location",
            headerName: "Current Location",
            width: 200,
          },
          { field: "Contact No.", headerName: "Contact No.", width: 150 },
          { field: "Job Id", headerName: "Job Id", width: 100 },
          { field: "Job Name", headerName: "Job Name", width: 200 },
        ];
        setColumns(buildColumns(rows, predefinedColumns));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, loading: false }));
    }
  };

  // Grid 2: Not Associated tile ke liye alag endpoint
  const fetchNotassociatedGrid = async (currentAxisId, pg) => {
    if (
      chartNumber.current !== 2 ||
      highlightedTile !== "Candidates not Associated with Job"
    )
      return;
    setLoading((prev) => ({ ...prev, loading: true }));
    const durations = timeDuration.EmployerPortalNotAssociated;
    setDataGridRecruiterId(currentAxisId);
    try {
      const response = await axios.post(
        `/dashboard/candidate/not-associated/${pg}`,
        { recruiter_id: currentAxisId, duration: durations },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      if (response.data.success) {
        const data = response.data.message;
        const formatDate = (isoDateString) => {
          const date = new Date(isoDateString);
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        };
        const rows = data.map((row, index) => {
          const updatedRow = formatRowFields(row);
          if (row["Date Created"])
            updatedRow["Date Created"] = formatDate(row["Date Created"]);
          return { uniqueId: `${index}-${row.id}`, id: row.id, ...updatedRow };
        });
        setGridData(rows);
        setDisableNext(data?.length < pageSize);
        setDisablePrev(pg === 1);
        setBreadcrumb([
          "Employer Portal",
          "Not Associated",
          "Recruiters",
          "Candidate List",
        ]);
        const predefinedColumns = [
          { field: "Candidate Name", headerName: "Candidate Name", width: 200 },
          { field: "Email", headerName: "Email", width: 300 },
          { field: "Contact No.", headerName: "Contact No.", width: 150 },
          { field: "Experience", headerName: "Experience", width: 150 },
          {
            field: "Current Location",
            headerName: "Current Location",
            width: 200,
          },
          { field: "Date Created", headerName: "Date Created", width: 200 },
        ];
        setColumns(buildColumns(rows, predefinedColumns));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, loading: false }));
    }
  };

  // ─────────────────────────── Utility Functions ───────────────────────────

  const formatRowFields = (row) => {
    const updatedRow = {};
    for (const key in row) {
      if (key === "Experience") {
        const exp = row[key];
        if (exp === "0" || exp === "0.0") {
          updatedRow[key] = "Fresher";
        } else if (exp) {
          const [years, months] = exp.split(".").map(Number);
          let str = "";
          if (years > 0) str += `${years} year${years !== 1 ? "s" : ""}`;
          if (months > 0)
            str += `${str ? " " : ""}${months} month${months !== 1 ? "s" : ""}`;
          updatedRow[key] = str || "NA";
        } else {
          updatedRow[key] = "NA";
        }
      } else {
        updatedRow[key] =
          row[key] === null || row[key] === "" ? "NA" : row[key];
      }
    }
    return updatedRow;
  };

  const buildColumns = (rows, predefinedColumns) =>
    (rows.length > 0 ? Object.keys(rows[0]) : predefinedColumns)
      .filter((key) => key !== "uniqueId")
      .map((key) => {
        const predefinedCol = predefinedColumns.find(
          (col) => col.field === key,
        ) || { headerName: key };
        return {
          field: key,
          headerName: predefinedCol.headerName,
          width: predefinedCol.width || 200,
        };
      });

  // ─────────────────────────── Pagination ──────────────────────────────────

  const handlePageChange = (newPage) => {
    setPage(newPage.page);
    if (
      chartNumber.current === 2 &&
      (highlightedTile === "Candidates Associated with Job" ||
        highlightedTile === "Total Candidates")
    ) {
      fetchCandidatestatusGrid(dataGridRecruiterId, newPage.page);
    } else if (
      chartNumber.current === 3 &&
      (highlightedTile === "Candidates Associated with Job" ||
        highlightedTile === "Total Candidates")
    ) {
      fetchstatusTypeGrid(statusIds, newPage.page);
    } else if (
      chartNumber.current === 2 &&
      highlightedTile === "Candidates not Associated with Job"
    ) {
      fetchNotassociatedGrid(dataGridRecruiterId, newPage.page);
    }
  };

  // ─────────────────────────── Breadcrumb ──────────────────────────────────

  const handleBreadcrumbClick = (index) => {
    const slicedBreadcrumb = breadcrumb.slice(0, index + 1);
    setBreadcrumb(slicedBreadcrumb);
    if (slicedBreadcrumb.length === 3) {
      setPage(1);
      setChartNum(1);
      fetchRecruiterchart();
    } else if (slicedBreadcrumb.length === 4) {
      setPage(1);
      setChartNum(2);
      fetchCandidatestatus(dataGridRecruiterId);
      fetchCandidatestatusGrid(dataGridRecruiterId, 1);
      if (highlightedTile === "Candidates not Associated with Job")
        fetchNotassociatedGrid(dataGridRecruiterId, 1);
    }
  };

  // ─────────────────────────── Chart Config ────────────────────────────────

  const maxYaxis = (data) => {
    const yaxisCount = { max: 5, tickcount: 5 };
    const maxCount = Math.max(...data.map((item) => item.count));
    if (maxCount + 1 <= 5) {
      yaxisCount.max = maxCount + 1;
      yaxisCount.tickcount = maxCount + 1;
    } else {
      yaxisCount.max = maxCount + Math.floor(maxCount / 5);
    }
    return yaxisCount;
  };

  const yaxisCount = maxYaxis(chartData);

  const Handleratio = () => {
    const ratio = 10 / chartData?.length;
    return ratio >= 1 ? null : ratio;
  };

  const getChartHeight = () => {
    const h = window.innerHeight;
    return h >= 900
      ? Math.round(h * 0.55)
      : h >= 700
        ? Math.round(h * 0.5)
        : Math.round(h * 0.45);
  };

  const getdatagridheight = () => {
    const h = window.innerHeight;
    return h >= 900
      ? Math.round(h * 0.55)
      : h >= 700
        ? Math.round(h * 0.5)
        : Math.round(h * 0.45);
  };

  const updatedChartData = chartData.map((item) => ({
    ...item,
    nameWithId: `${item?.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} (${item.id})`,
  }));

  const axisTitle =
    breadcrumb.length === 3 ? "Recruiters" : "Candidates Status";

  const displayChartdata = () => ({
    data: updatedChartData,
    xField: "name",
    yField: "count",
    height: getChartHeight(),
    style: { fill: "#1976d2", radiusTopLeft: 6, radiusTopRight: 6 },
    scrollbar: { x: { ratio: Handleratio() } },
    scale: {
      y: { tickCount: yaxisCount.tickcount, domainMax: yaxisCount.max },
    },
    animation: { enter: { duration: 0 }, exit: { duration: 0 } },
    axis: {
      x: {
        labelSpacing: 8,
        labelFontSize: 14,
        labelFontWeight: 600,
        labelAlign: "center",
        labelTransform: "Capitalize",
        labelFormatter: (val) => {
          const mapping = {
            "Offer Accepted (2000)": "Off. Acc.",
            "Offer Rejected (3000)": "Off. Rej.",
            "Offer Released (2500)": "Off. Rel.",
            "Tech Evaluation (2200)": "Tech. Eval.",
            "Manager Evaluation (2300)": "Man. Eval.",
          };
          if (mapping[val]) return mapping[val];
          const parts = val.split(" ")[0];
          return parts.length > 1 ? parts : val;
        },
        title: axisTitle,
        line: { style: { stroke: "#000", lineWidth: 1 } },
        grid: null,
      },
      y: {
        title: "No. of Candidates",
        labelSpacing: 4,
        labelFontSize: 14,
        labelFontWeight: 500,
        line: { style: { stroke: "#000", lineWidth: 1 } },
        grid: null,
      },
    },
    onReady: ({ chart }) => {
      chart.on("interval:click", (ev) => {
        if (chartNumber.current < 3) {
          const nextLevel = chartNumber.current + 1;
          setChartNum(nextLevel);
          if (
            nextLevel === 2 &&
            (highlightedTile === "Candidates Associated with Job" ||
              highlightedTile === "Total Candidates")
          ) {
            fetchCandidatestatus(ev.data.data.id);
            fetchCandidatestatusGrid(ev.data.data.id, 1);
          } else if (
            nextLevel === 3 &&
            (highlightedTile === "Candidates Associated with Job" ||
              highlightedTile === "Total Candidates")
          ) {
            fetchstatusTypeGrid(ev.data.data.id, 1);
          } else if (
            nextLevel === 2 &&
            highlightedTile === "Candidates not Associated with Job"
          ) {
            fetchNotassociatedGrid(ev.data.data.id, 1);
          }
        } else {
          setChartNum(1);
        }
      });
    },
  });

  // ─────────────────────────── Row / Activity Click ────────────────────────

  const handleRowClick = (row) => {
    window.open(`/Manage-Candidates/${row.id}`, "_blank");
  };

  const handleActivityClick = (item) => {
    if (!item) return;
    const id = item?.id || null;
    if (!id) return;
    const comment = item?.comment?.toLowerCase() || "";
    if (
      comment.includes("shortlisted") ||
      comment.includes("applied") ||
      comment.includes("candidate")
    ) {
      nav(`/Manage-Candidates/${id}?tab=0`);
      return;
    }
    if (comment.includes("job")) {
      nav(`/jobOrders/${id}`);
      return;
    }
    nav(`/Manage-Candidates/${id}?tab=0`);
  };

  // ─────────────────────────── Derived render flag ─────────────────────────

  const showGrid =
    (chartLevel === 2 &&
      highlightedTile === "Candidates not Associated with Job") ||
    chartLevel === 3;

  // ─────────────────────────── Loading states ──────────────────────────────

  // First load of chart area → shimmer; subsequent → CircularProgress
  const showChartShimmer = !chartFirstLoadDone && loading.loading;
  const showChartCircular = chartFirstLoadDone && loading.loading;

  // Activities: always shimmer until first load done
  const showActivityShimmer = loading.activityLoading;

  // ─────────────────────────── JSX ─────────────────────────────────────────

  return (
    <Box sx={{ height: "100%" }}>
      <Grid container sx={{ height: "100%" }}>
        {/* ── Left: Chart / DataGrid area ── */}
        <Grid item xs={8}>
          <Box sx={{ mt: 0, mr: 2, borderRadius: "10px", height: "100%" }}>
            <Paper
              elevation={0}
              style={{ borderRadius: "10px", height: "100%" }}
            >
              {/* Header: Breadcrumbs — hide during first-load shimmer */}
              {!showChartShimmer && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#F9F9F9",
                    padding: "8px 16px",
                    borderRadius: "10px 10px 0 0",
                    minHeight: "45px",
                  }}
                >
                  <Breadcrumbs
                    separator={
                      <Typography
                        sx={{
                          color: "#9AA2B1",
                          fontWeight: 400,
                          fontSize: "1rem",
                        }}
                      >
                        ›
                      </Typography>
                    }
                    sx={{
                      "& .MuiBreadcrumbs-ol": {
                        flexWrap: "nowrap",
                        alignItems: "center",
                      },
                    }}
                  >
                    {breadcrumb.map((crumb, index) =>
                      index === 0 || index === 1 ? (
                        <Typography
                          key={index}
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 400,
                            color: "#9AA2B1",
                            lineHeight: 1.4,
                          }}
                        >
                          {crumb}
                        </Typography>
                      ) : (
                        <Typography
                          key={index}
                          onClick={() => handleBreadcrumbClick(index)}
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 700,
                            color:
                              index === breadcrumb.length - 1
                                ? "#3F95EA"
                                : "#1A202C",
                            cursor: "pointer",
                            lineHeight: 1.4,
                            transition: "color 0.15s",
                            "&:hover": { color: "#3F95EA" },
                          }}
                        >
                          {crumb}
                        </Typography>
                      ),
                    )}
                  </Breadcrumbs>
                </Box>
              )}

              {/* Content area */}
              {showChartShimmer ? (
                // First load → shimmer fills entire panel including where breadcrumb would be
                <Box
                  sx={{
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Breadcrumb shimmer */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 2,
                      backgroundColor: "#F9F9F9",
                      p: "8px 16px",
                      borderRadius: "10px 10px 0 0",
                      minHeight: "45px",
                    }}
                  >
                    <ShimmerBox width={90} height={14} borderRadius={4} />
                    <ShimmerBox width={8} height={10} borderRadius={2} />
                    <ShimmerBox width={70} height={14} borderRadius={4} />
                    <ShimmerBox width={8} height={10} borderRadius={2} />
                    <ShimmerBox width={80} height={14} borderRadius={4} />
                  </Box>
                  {/* Chart shimmer */}
                  <Box sx={{ flex: 1, px: 1 }}>
                    <CandidateChartShimmer />
                  </Box>
                </Box>
              ) : showChartCircular ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: getdatagridheight(),
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : showGrid ? (
                <Box
                  sx={{
                    height: getdatagridheight(),
                    width: "100%",
                    padding: "12px",
                  }}
                >
                  <DynamicDataGrid
                    rows={gridData}
                    columns={columns}
                    loading={loading.loading}
                    totalRows={
                      disableNext
                        ? (page - 1) * pageSize + gridData.length
                        : page * pageSize + 1
                    }
                    paginationModel={{ page: page - 1, pageSize }}
                    onPaginationModelChange={(model) =>
                      handlePageChange({ page: model.page + 1 })
                    }
                    onRowSelection={handleRowClick}
                    hideFooter={false}
                    showCheckbox={false}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    borderRadius: "10px",
                    flex: 1,
                    height: "100%",
                    minHeight: 0,
                  }}
                >
                  <Column className="dashboardCharts" {...displayChartdata()} />
                </Box>
              )}
            </Paper>
          </Box>
        </Grid>

        {/* ── Right: Activities panel ── */}
        <Grid item xs={4} sx={{ height: "100%" }}>
          <Paper
            elevation={0}
            sx={{ height: "100%", borderRadius: "10px", border: "none" }}
          >
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: 2,
                display: "flex",
                height: "100%",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "16px",
                  background: "#F9F9F9",
                  p: 2,
                  borderRadius: "8px 8px 0 0",
                }}
                textAlign="start"
              >
                Recent Activities
              </Typography>
              <Box
                sx={{
                  height: "100%",
                  position: "relative",
                  overflowY: "auto",
                  px: 1.5,
                  pl: 2.5,
                  pb: 2.5,
                }}
              >
                {/* Activity shimmer always shows until first activities load */}
                {showActivityShimmer ? (
                  <ActivityShimmer />
                ) : (
                  <Activities
                    activityData={activities}
                    loading={false}
                    onItemClick={handleActivityClick}
                  />
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CandidateDashboard;
